export const PERFORMER_SEGMENT = {
	TOP: 'top',
	TYPICAL: 'typical',
	BOTTOM: 'bottom',
} as const;

export type PerformerSegmentKey = typeof PERFORMER_SEGMENT[keyof typeof PERFORMER_SEGMENT];

export interface PerformerSegmentationInput {
	id: string | number;
	responseValue: number;
	resolutionValue: number;
}

const RESPONSE_WEIGHT = 0.6;
const RESOLUTION_WEIGHT = 0.4;
const TOP_QUANTILE = 0.25;
const BOTTOM_QUANTILE = 0.75;

export const percentileRank = (sortedValues: number[], value: number): number => {
	if (sortedValues.length === 0) return 0;
	let belowCount = 0;
	let equalCount = 0;
	for (let index = 0; index < sortedValues.length; index++) {
		if (sortedValues[index] < value) {
			belowCount++;
		} else if (sortedValues[index] === value) {
			equalCount++;
		}
	}
	return (belowCount + 0.5 * equalCount) / sortedValues.length;
};

export const weightedPerformerScore = (
	responsePctRank: number,
	resolutionPctRank: number
): number => RESPONSE_WEIGHT * responsePctRank + RESOLUTION_WEIGHT * resolutionPctRank;

export const quantileOfSorted = (sortedValues: number[], quantile: number): number => {
	if (sortedValues.length === 0) return 0;
	const position = (sortedValues.length - 1) * quantile;
	const lowerIndex = Math.floor(position);
	const upperIndex = Math.ceil(position);
	if (lowerIndex === upperIndex) return sortedValues[lowerIndex];
	const fraction = position - lowerIndex;
	return (
		sortedValues[lowerIndex] + fraction * (sortedValues[upperIndex] - sortedValues[lowerIndex])
	);
};

export const segmentSupplierPerformers = (
	rows: PerformerSegmentationInput[]
): Map<string | number, PerformerSegmentKey> => {
	const segmentById = new Map<string | number, PerformerSegmentKey>();
	if (!rows || rows.length === 0) return segmentById;

	const responseValues = rows.map((row) => row.responseValue).sort((a, b) => a - b);
	const resolutionValues = rows.map((row) => row.resolutionValue).sort((a, b) => a - b);

	const scored = rows.map((row) => {
		const responsePctRank = percentileRank(responseValues, row.responseValue);
		const resolutionPctRank = percentileRank(resolutionValues, row.resolutionValue);
		return {
			id: row.id,
			score: weightedPerformerScore(responsePctRank, resolutionPctRank),
		};
	});

	const sortedScores = scored.map((entry) => entry.score).sort((a, b) => a - b);
	const topThreshold = quantileOfSorted(sortedScores, TOP_QUANTILE);
	const bottomThreshold = quantileOfSorted(sortedScores, BOTTOM_QUANTILE);
	const hasScoreSpread = topThreshold < bottomThreshold;
	const tailCap = Math.ceil(rows.length * TOP_QUANTILE);
	const orderedByScore = scored
		.slice()
		.sort(
			(entryA, entryB) =>
				entryA.score - entryB.score || String(entryA.id).localeCompare(String(entryB.id))
		);

	const topIds = new Set<string | number>();
	const bottomIds = new Set<string | number>();
	if (hasScoreSpread) {
		for (let index = 0; index < orderedByScore.length; index++) {
			const entry = orderedByScore[index];
			if (topIds.size >= tailCap || entry.score > topThreshold) break;
			topIds.add(entry.id);
		}
		for (let index = orderedByScore.length - 1; index >= 0; index--) {
			const entry = orderedByScore[index];
			if (bottomIds.size >= tailCap || entry.score < bottomThreshold) break;
			if (topIds.has(entry.id)) continue;
			bottomIds.add(entry.id);
		}
	}

	scored.forEach((entry) => {
		if (topIds.has(entry.id)) {
			segmentById.set(entry.id, PERFORMER_SEGMENT.TOP);
		} else if (bottomIds.has(entry.id)) {
			segmentById.set(entry.id, PERFORMER_SEGMENT.BOTTOM);
		} else {
			segmentById.set(entry.id, PERFORMER_SEGMENT.TYPICAL);
		}
	});

	return segmentById;
};
